/**
 * Chladni Patterns / Cymatics Visualizer
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class ChladniVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'chladni',
    name: 'Chladni Patterns',
    description: 'Cymatics-inspired interference patterns',
    category: 'physics',
    defaultZoom: 0.6,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, activeWaves, speed, time, zoom, pan, uploadedAudioData, isUploadedAudioPlaying } = config;
    const cx = width / 2;
    const cy = height / 2;

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      return;
    }

    const imgData = ctx.createImageData(width, height);
    const data = imgData.data;

    let totalAmp = displayWaves.reduce((sum, w) => sum + w.amp, 0) * 2;
    totalAmp = totalAmp === 0 ? 1 : totalAmp;
    const invTotalAmp = 1 / totalAmp;

    const baseScale = height / 2;
    const currentScale = baseScale * zoom;
    const invScale = 1 / currentScale;

    // Pre-compute wave parameters
    const waveParams = displayWaves.map(w => {
      const effectiveFreq = w.freq * speed;
      const angleBase = (w.phase * Math.PI / 180) - (time * effectiveFreq * 5);
      return {
        amp: w.amp,
        spatialFreq: effectiveFreq * 2.0 * Math.PI,
        cosAngle: Math.cos(angleBase),
        sinAngle: Math.sin(angleBase)
      };
    });

    // Color coefficients
    const colorR = 245, colorG = 208, colorB = 130;
    const baseR = 10, baseG = 12, baseB = 20;

    for (let y = 0; y < height; y++) {
      const ny = (y - cy - pan.y) * invScale;
      let index = y * width * 4;

      for (let x = 0; x < width; x++) {
        const nx = (x - cx - pan.x) * invScale;

        let reSum = 0;
        let imSum = 0;

        for (let i = 0; i < waveParams.length; i++) {
          const w = waveParams[i];
          const spatialVal = Math.cos(w.spatialFreq * nx) + Math.cos(w.spatialFreq * ny);
          const ampSpatial = w.amp * spatialVal;
          reSum += ampSpatial * w.cosAngle;
          imSum += ampSpatial * w.sinAngle;
        }

        const magSquared = reSum * reSum + imSum * imSum;
        const magnitude = Math.sqrt(magSquared);
        const normalizedAmp = magnitude * invTotalAmp;
        const intensity = Math.pow(Math.min(1, normalizedAmp), 1.5);

        data[index++] = baseR + (colorR * intensity);
        data[index++] = baseG + (colorG * intensity);
        data[index++] = baseB + (colorB * intensity);
        data[index++] = 255;
      }
    }

    ctx.putImageData(imgData, 0, 0);
  }
}
